import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Phone, Mail, MapPin, Clock, Send, CheckCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: "Téléphone",
    value: "06 95 44 27 88",
    link: "tel:0695442788",
    description: "Du lundi au samedi, 8h-19h",
  },
  {
    icon: Mail,
    title: "Email",
    value: "myvitreo@outlook.fr",
    link: "mailto:myvitreo@outlook.fr",
    description: "Réponse sous 24h",
  },
  {
    icon: MapPin,
    title: "Zone d'intervention",
    value: "Lorraine & Grand Est",
    description: "Basé à Metz",
  },
  {
    icon: Clock,
    title: "Horaires",
    value: "Lun-Sam: 8h-19h",
    description: "Intervention sur rendez-vous",
  },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Erreur lors de l'envoi");

      setIsSuccess(true);
      toast.success("Votre message a été envoyé avec succès !");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="contact-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              Contactez-nous
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Besoin d'un renseignement ?
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Notre équipe est à votre écoute pour répondre à toutes vos questions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 rounded-2xl card-hover"
              >
                <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-sky-400" />
                </div>
                <h3 className="font-semibold text-white mb-1">{info.title}</h3>
                {info.link ? (
                  <a 
                    href={info.link}
                    className="text-sky-400 font-medium hover:text-sky-700 transition-colors"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-sky-400 font-medium">{info.value}</p>
                )}
                <p className="text-sm text-slate-500 mt-1">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {isSuccess ? (
                <div className="glass-card p-12 rounded-3xl text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Message envoyé !
                  </h2>
                  <p className="text-slate-300 mb-8">
                    Nous avons bien reçu votre message et vous répondrons dans les plus brefs délais.
                  </p>
                  <Button
                    onClick={() => {
                      setIsSuccess(false);
                      setFormData({ name: "", phone: "", email: "", subject: "", message: "" });
                    }}
                    className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8"
                  >
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="glass-card p-8 rounded-3xl">
                  <h2 className="text-2xl font-bold text-white mb-6">
                    Envoyez-nous un message
                  </h2>

                  <div className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="mb-2 block">Nom complet</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Jean Dupont"
                          required
                          className="bg-slate-800/50"
                          data-testid="contact-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="mb-2 block">Téléphone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="06 00 00 00 00"
                          required
                          className="bg-slate-800/50"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="mb-2 block">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="jean.dupont@email.com"
                        required
                        className="bg-slate-800/50"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="mb-2 block">Sujet</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Objet de votre message"
                        required
                        className="bg-slate-800/50"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="mb-2 block">Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Votre message..."
                        required
                        className="bg-slate-800/50 min-h-[150px]"
                        data-testid="contact-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 text-lg font-semibold"
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2 justify-center">
                          <div className="spinner" />
                          Envoi en cours...
                        </span>
                      ) : (
                        <>
                          <Send className="mr-2 w-5 h-5" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>

            {/* Map & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              <div className="glass-card p-6 rounded-3xl">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Zone d'intervention
                </h3>
                <p className="text-slate-300 mb-6">
                  Basé à Metz, MY VITREO intervient dans toute la région Lorraine et le Grand Est. 
                  Nos techniciens se déplacent directement chez vous ou sur votre lieu de travail.
                </p>
                <div className="map-container rounded-2xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d330022.0477426665!2d6.0!3d49.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4794dc09d8d63e35%3A0x406f69c2f3d9f80!2sMetz!5e0!3m2!1sfr!2sfr!4v1710000000000"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Zone d'intervention MY VITREO"
                  ></iframe>
                </div>
              </div>

              <div className="glass-card p-6 rounded-3xl">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Villes desservies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Metz", "Thionville", "Nancy", "Forbach", "Sarreguemines", "Amnéville", "Épinal", "Pont-à-Mousson", "Sarrebourg", "Briey"].map((city) => (
                    <span
                      key={city}
                      className="px-3 py-1.5 bg-sky-500/20 text-sky-700 rounded-full text-sm font-medium"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
